import React, { useState } from "react"

import { useTranslation } from "react-i18next"

import { Grid } from "@mui/material"
import { Pagination, Typography } from "@synapse-analytics/synapse-ui"
import type { MRT_ColumnDef } from "material-react-table"

import type { ScriptSchemaFeature } from "../../types/generated/api/ScriptSchemaFeature"
import { Tick } from "../status/Tick"
import { BaseTable } from "./BaseTable"

type Props = {
  featuresList: Array<ScriptSchemaFeature>
}

/**
 * Table view to display all current template features (input and output/expected)
 * @param {Array<ScriptSchemaFeature>} featuresList list of retrieved features (input and output)
 * @returns {React.ReactElement}
 */
export function TemplateFeaturesTable(props: Props): React.ReactElement {
  const { featuresList } = props

  const { t } = useTranslation("tables", {
    keyPrefix: "TemplateFeaturesTable",
  })

  const rowsPerPage = 5

  // Pagination and search state for table view
  const [page, setPage] = useState<number>(0)

  const paginatedFeaturesList = featuresList
    // First, paginate the features by slicing based on current page and rows per page
    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

  // columns for template features (input and output) table
  const columns: MRT_ColumnDef<ScriptSchemaFeature>[] = [
    {
      header: t("featureName"),
      accessorKey: "name",
      Cell: ({ row }) => <Typography variant="p">{paginatedFeaturesList[row.index]?.name}</Typography>,
    },
    {
      header: t("type"),
      accessorKey: "type",
      Cell: ({ row }) => (
        <Typography style={{ textTransform: "capitalize" }} variant="p">
          {paginatedFeaturesList[row.index]?.type?.toLowerCase()}
        </Typography>
      ),
    },
    {
      header: t("required"),
      accessorKey: "required",
      Cell: ({ row }) => <Tick variant={paginatedFeaturesList[row.index]?.is_required ? "positive" : "base"} />,
    },
  ]

  return (
    <Grid item container gap={2} xs direction={"column"} flexWrap={"nowrap"}>
      <Grid item width={"100%"}>
        <BaseTable
          newStyling
          disablePagination
          data={paginatedFeaturesList ?? []}
          columns={columns as MRT_ColumnDef[]}
          disableToolbar
          title={t("title")}
          pageSize={rowsPerPage}
          enableOrdering={false}
          isLoading={false}
        />
      </Grid>

      {featuresList.length > 0 && (
        <Pagination
          page={page + 1}
          onPageChange={(page: number) => setPage(page - 1)}
          pageSize={rowsPerPage}
          totalCount={featuresList.length}
          variant={"simple"}
          size="small"
          hidePageSize
          maxVisiblePages={5}
        />
      )}
    </Grid>
  )
}
